//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeUnits, useRealtimeDrivers } from 'Core/hooks';
import { useIntl } from "react-intl";

//Actions
import { manageEditManageFuelCard, reset } from "./redux/slice";

//Controls
import { Button, Form, InputNumeric, Modal, Select } from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationManager, NotificationContainer } from "Components/Notifications";

const EditManageFuelCard = ({ refresh }) => {
  //Language
  const { messages, locale } = useIntl();

  //Constants
  const TYPE_OF_ASSIGNMENT_OPTIONS = [
    {
      id: "unassignment",
      label: messages["manage_editFuelCard_typeOfAssignment_unassignment"],
      disabled: false
    },
    {
      id: "unit",
      label: messages["manage_editFuelCard_typeOfAssignment_unit"],
      disabled: false
    },
    {
      id: "driver",
      label: messages["manage_editFuelCard_typeOfAssignment_driver"],
      disabled: false
    }
  ];

  const FORM_INIT_STATE = {
    typeOfAssignment: TYPE_OF_ASSIGNMENT_OPTIONS[0],
    unit: null,
    driver: null,
    tankSize: 0
  };

  //States
  const [formData, setFormData] = useState(FORM_INIT_STATE);

  //Form refs
  const createFuelCardFormRef = Form.useForm();

  //Redux
  const dispatch = useDispatch();
  const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
  const unitsList = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const loadingUnitsList = useSelector((state) => state.catalogsRedux.loadingUnitsList);
  const loadingDrivers = useSelector((state) => state.catalogsRedux.loadingDrivers);
  const { fuelCard, loading, error, updateManageFuelCardResult, open } = useSelector(
    (state) => state.manageEditManageFuelCardRedux
  );

  const handleClose = useCallback(() => {
    dispatch(reset());
  }, []);

  if (error) {
    NotificationManager.error(
      error?.error || messages["errorMessage"],
      messages["error"],
      4000,
      null,
      null,
      "error"
    );
    dispatch(reset({ error: null }));
  }

  if (updateManageFuelCardResult?.id) {
    NotificationManager.success(
      messages["manage_editFuelCard_success"],
      messages["success"],
      3000,
      null,
      null,
      "success"
    );
    refresh();
    handleClose();
  }

  //Memos
  const title = useMemo(
    () => `${messages["manage_editFuelCard_modalTitle"]}: "${fuelCard?.cardNumber}"`,
    [fuelCard, messages]
  );

  const unitsSelectorRules = useMemo(() => [
    {
      required: formData.typeOfAssignment?.id === "unit",
      message: messages["required_field"]
    }
  ], [formData.typeOfAssignment, locale]);

  const driversSelectorRules = useMemo(() => [
    {
      required: formData.typeOfAssignment?.id === "driver",
      message: messages["required_field"]
    }
  ], [formData.typeOfAssignment, locale]);

  const handleEditManageFuelCardResult = useCallback(
    (values) => {

      if (values.tankSize > 1000) {
        NotificationManager.error(
          messages["manage_editFuelCard_tankSize_error"],
          messages["error"],
          4000,
          null,
          null,
          "error"
        );
        return;
      }


      if (
        !values ||
        (values.typeOfAssignment?.id === "unit" && !values.unit?.id) ||
        (values.typeOfAssignment?.id === "unit" && values.tankSize < 0.0) ||
        (values.typeOfAssignment?.id === "driver" && !values.driver?.driverId)
      )
        return;

      const { typeOfAssignment, unit, driver, tankSize } = values;
      const newData = {
        id: fuelCard?.id,
        unitId: unit?.id,
        driverId: driver?.driverId,
        tankSize: tankSize || 0
      };
      if (typeOfAssignment.id === "unassignment") {
        newData.unitId = null;
        newData.driverId = null;
        newData.tankSize = 0;
      } else if (typeOfAssignment.id === "unit") {
        newData.driverId = null;
      } else if (typeOfAssignment.id === "driver") {
        newData.unitId = null;
        newData.tankSize = 0;
      }
      dispatch(manageEditManageFuelCard(newData));
    },
    [fuelCard]
  );

  useEffect(() => {
    if (!open || !fuelCard) return;
    const { driver, unit } = fuelCard;

    let typeOfAssignment = "unassignment";
    if (driver?.driverId) typeOfAssignment = "driver"
    else if (unit?.id) typeOfAssignment = "unit";
    const defaultData = {
      typeOfAssignment: TYPE_OF_ASSIGNMENT_OPTIONS.find((option) => option.id === typeOfAssignment),
      unit: unit?.id ? unit : null,
      driver: driver?.driverId ? driver : null,
      tankSize: fuelCard?.unit?.tankSize || 0
    }
    setFormData(defaultData);
    setTimeout(() => {
      createFuelCardFormRef.setValues(defaultData);
    }, 600)
  }, [fuelCard, open, drivers]);

  return (
    <Modal
      title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading || loadingUnitsList || loadingDrivers}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              createFuelCardFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        <Form
          ref={createFuelCardFormRef.ref}
          loading={loading || loadingUnitsList || loadingDrivers}
          onSubmit={(values) => {
            handleEditManageFuelCardResult(values)
          }
          }
          onChangeValues={() => { }}
        >
          <Form.Item
            name="typeOfAssignment"
            label={messages["manage_editFuelCard_typeOfAssignmentSelectLabel"]}
          >
            <Select
              key={"typeOfAssignment"}
              style={{ width: "100%" }}
              isLoading={loading || loadingUnitsList || loadingDrivers}
              id="typeOfAssignment"
              isDisabled={loading || loadingUnitsList || loadingDrivers}
              options={TYPE_OF_ASSIGNMENT_OPTIONS}
              textField="label"
              valueField="id"
              value={formData.typeOfAssignment}
              onChange={(value) => {
                createFuelCardFormRef?.ref?.current?.setValues({
                  typeOfAssignment: value
                });
                setFormData({ ...formData, typeOfAssignment: value });

              }}
              isSearchable
            />
          </Form.Item>
          {formData.typeOfAssignment?.id === "unit" && (
            <>
              <Form.Item
                label={messages["manage_editFuelCard_typeOfAssignment_unit"]}
                name="unit"
                rules={unitsSelectorRules}
              >
                <Select
                  id="unit"
                  name="unit"
                  isLoading={loadingUnitsList || loading}
                  isDisabled={loadingUnitsList || loading}
                  options={unitsList}
                  textField="label"
                  valueField="id"
                  value={formData.unit}
                  onChange={(value) => {
                    createFuelCardFormRef?.ref?.current?.setValues({
                      unit: value
                    });
                    setFormData({ ...formData, unit: value?.id ? value : null })
                  }}
                  isSearchable
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                label={messages["manage_editFuelCard_tankSize"]}
                name="tankSize"
                rules={[
                  {
                    max: 1000,
                    message: `${messages["max"]}: 1000`
                  },
                  {
                    min: 0.0,
                    message: `${messages["min"]}: 0.0`
                  }
                ]}
              >
                <InputNumeric
                  id="tankSize"
                  decimalPlaces={1}
                  placeholder={"0.0"}
                  maxLength={12}
                  preventOnChange
                  min={0}
                  onChange={(value) => {
                    setFormData({
                      ...formData,
                      tankSize: value
                    });
                  }}
                  value={formData.tankSize}
                />
              </Form.Item>
            </>
          )}
          {formData.typeOfAssignment?.id === "driver" && (
            <Form.Item
              label={messages["manage_editFuelCard_typeOfAssignment_driver"]}
              name="driver"
              rules={driversSelectorRules}
            >
              <Select
                id="driver"
                isLoading={loadingDrivers || loading}
                isDisabled={loadingDrivers || loading}
                options={drivers}
                textField="name"
                valueField="driverId"
                value={formData.driver?.unitId ? formData.driver : null}
                onChange={(value) => {
                  createFuelCardFormRef?.ref?.current?.setValues({
                    driver: value
                  });
                  setFormData({ ...formData, driver: value })
                }}
                isSearchable
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default EditManageFuelCard;
